"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { Check, LucideIcon } from "lucide-react";
import { cn } from "./utils";

export interface MobilePickerOption<T = string> {
  value: T;
  label: string;
  description?: string;
  color?: string;
  bgColor?: string;
  borderColor?: string;
  icon?: LucideIcon;
}

interface MobilePickerProps<T = string> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  icon?: LucideIcon;
  options: MobilePickerOption<T>[];
  value: T;
  onChange: (value: T) => void;
  renderOption?: (option: MobilePickerOption<T>, isSelected: boolean) => React.ReactNode;
}

/**
 * MobilePicker - A reusable mobile-optimized drawer picker component
 * 
 * This component provides a consistent bottom drawer experience for mobile
 * with proper positioning, animations, and accessibility.
 * 
 * Features:
 * - Mobile-optimized bottom drawer with drag handle
 * - Simple mode: Clean list with checkmarks (default)
 * - Rich mode: Custom rendering via renderOption prop
 * - Smooth animations and transitions
 * - Backdrop dismiss and option selection
 */
export function MobilePicker<T = string>({
  open,
  onOpenChange,
  title,
  icon: Icon,
  options,
  value,
  onChange,
  renderOption,
}: MobilePickerProps<T>) {
  const handleSelect = (optionValue: T) => {
    onChange(optionValue);
    onOpenChange(false);
  };

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        {/* Overlay */}
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        
        {/* Drawer Content */}
        <DrawerPrimitive.Content
          className="fixed z-50 flex flex-col bg-white max-h-[45vh]"
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.12)',
          }}
        >
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>
          
          {/* Header */}
          <div className="px-4 pt-2.5 pb-3 border-b border-gray-100">
            <DrawerPrimitive.Title className="flex items-center justify-center gap-2 text-base font-semibold text-gray-900">
              {Icon && <Icon className="w-4 h-4 text-purple-600" />}
              {title}
            </DrawerPrimitive.Title>
          </div>
          
          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {options.map((option, index) => {
              const isSelected = value === option.value;
              
              // Use custom renderer if provided
              if (renderOption) {
                return (
                  <div key={String(option.value)} onClick={() => handleSelect(option.value)}>
                    {renderOption(option, isSelected)}
                  </div>
                );
              }
              
              // Default simple renderer
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3 transition-colors",
                    isSelected
                      ? "bg-purple-50"
                      : "hover:bg-gray-50 active:bg-gray-100",
                    index !== options.length - 1 && "border-b border-gray-50"
                  )}
                >
                  <span
                    className={cn(
                      "text-sm",
                      isSelected
                        ? "text-purple-700 font-medium"
                        : "text-gray-700"
                    )}
                  >
                    {option.label}
                  </span>
                  {isSelected && (
                    <Check className="w-4 h-4 text-purple-600" />
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Safe area padding for mobile */}
          <div className="h-4" />
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
